/**
 * EL LISTADO DE INVARIANTES, SIN CORRER NINGUNA.
 *
 * Qué problema resuelve. La consola sólo sabía qué invariantes existen DESPUÉS de
 * que el gate terminara, porque la lista venía dentro del informe. Mientras el
 * gate corre —un minuto largo contra la base viva— la pantalla no podía enseñar
 * el listado, así que el operador no sabía cuántas son ni cuáles faltan. Este
 * fichero es esa lista: sólo el `id` y el título de cada una, para pintarlas en
 * gris desde el instante en que se pulsa «Ejecutar».
 *
 * Por qué está aquí, en `src/` de functions, y no en el front. Porque lo importa
 * el navegador Y lo verifica la suite de backend: el front lo trae con
 * `@functions/...` (el mismo camino por el que ya trae `packageIds.ts`) y
 * `tests/network-reset/invariantCatalog.spec.ts` comprueba, invariante a
 * invariante y EN ORDEN, que dice exactamente lo mismo que los motores de verdad
 * (`INVARIANTS` y `unfreezeInvariants`). Una lista paralela que nadie compara es
 * justo la deriva que esta consola existe para evitar; comparada, es un simple
 * índice.
 *
 * NO SE IMPORTA NADA AQUÍ, y es a propósito: `invariants.ts` arrastra
 * `firebase-admin` (vía `aliasInvariant`), que no puede entrar al bundle del
 * navegador. Este fichero son dos arrays de strings y nada más.
 *
 * Y ESTO ES UN ESQUELETO, NO LA VERDAD DEL INFORME. Cuando el informe llega, la
 * pantalla pinta el título QUE TRAE EL INFORME para cada fila. Esta lista sólo
 * decide qué filas se ven antes de correr, y de qué se dan cuenta las pantallas
 * si un día el backend desplegado tiene una invariante que aquí no está (se
 * añade al final, nunca se oculta).
 */

export interface InvariantCatalogEntry {
  id: string;
  title: string;
}

/**
 * EL GATE DEL PASO 18 — el estructural, el que decide «descongelar o rollback».
 * Espejo de `INVARIANTS` (`invariants.ts`), en su mismo orden, que es además el
 * orden en que se ejecutan: `runChecks` las recorre una detrás de otra.
 */
export const GATE_INVARIANT_CATALOG: InvariantCatalogEntry[] = [
  { id: "INV-01", title: "positioned flags are present booleans on every user" },
  { id: "INV-02", title: "positioned.unilevel users have a unilevelTree node" },
  { id: "INV-03", title: "positioned.binary users have a binaryTree node" },
  {
    id: "INV-04",
    title: "day-1 default: users.enroller === unilevelTree.parent (D6)",
  },
  {
    id: "INV-05",
    title: "unilevelTree.parent resolves to root or an existing node",
  },
  {
    id: "INV-06",
    title: "unilevelTree.enroller resolves to root or an existing user",
  },
  {
    id: "INV-07",
    title: "binaryTree.parent resolves to root or an existing node",
  },
  {
    id: "INV-08",
    title: "binary parent backlinks to the child (childLeft/childRight)",
  },
  {
    id: "INV-09",
    title: "binary side matches which parent pointer holds the child",
  },
  {
    id: "INV-10",
    title: "unilevelTree.uplines equals the parent chain to root",
  },
  {
    id: "INV-11",
    title: "legacy preserved before the prune — every user carries resetSweptAt",
  },
  {
    id: "INV-12",
    title:
      "placed users have non-empty enroller and unilevel parent (anti-empty §15-F)",
  },
  {
    id: "INV-13",
    title: "directs counters: activated <= count, lite/vip <= activated",
  },
  {
    id: "INV-14",
    title: "directsCount/directsActivated equal the placed child count (day-1)",
  },
  {
    id: "INV-15",
    title: "currentRank is an active rank in ranksConfiguration",
  },
  {
    id: "INV-16",
    title: "all reset to INITIAL with ranksCarrerData date = migration day",
  },
  {
    id: "INV-17",
    title:
      "ranksConfiguration has a sentinel above >=2 active tiers (ranksOrdered[index+2] safe)",
  },
  {
    id: "INV-18",
    title: "placed users have registeredAt (original registration date, §6)",
  },
  { id: "INV-19", title: "scholarship preserved (not reset, §5/§15-D)" },
  {
    id: "INV-20",
    title: "binaryTree parents index matches the binary parent chain",
  },
  {
    id: "INV-21",
    title: "every user carries resetSweptAt (=> it is safe to delete basic)",
  },
  {
    id: "INV-22",
    title: "networkData no longer exists for any user (at ANY depth)",
  },
  {
    id: "INV-23",
    title: "everyone who had a scholarship has it in userIncentives, same data",
  },
  {
    id: "INV-24",
    title: "account aliases: transparent, canonical placed, no chains",
  },
  {
    id: "INV-25",
    title: "every unilevelTree node has a users/{u} doc (node => user)",
  },
  {
    id: "INV-26",
    title: "every binaryTree node has a users/{u} doc (node => user)",
  },
  {
    id: "INV-27",
    title:
      "binary childLeft/childRight resolve to a node that points back (parent side)",
  },
  {
    id: "INV-28",
    title:
      "every ACTIVE member in the cut is placed (completeness, not consistency)",
  },
  {
    id: "INV-29",
    title:
      "CMMI: every census member is placed in mmiTree and the pay-walk terminator exists",
  },
  {
    id: "INV-30",
    title:
      "CMMI: every mmiTree node maps to a users/{u} doc and to a resolvable parent",
  },
  {
    id: "INV-31",
    title:
      "rankPoints ledgers are seeded and internally coherent after the #579 dispersion",
  },
  {
    id: "INV-32",
    title:
      "the #579 rank dispersion reached every ACTIVE member whose plan disperses points",
  },
  {
    id: "INV-33",
    title: "the LEGACY `binary-tree` collection is gone from the live database",
  },
];

/**
 * EL GATE DE DESCONGELAR — el segundo, diez pasos más tarde. Espejo de
 * `unfreezeInvariants` (`System/Migration/unfreezeGate.ts`), en su orden.
 *
 * Sus títulos se arman con constantes del código (`COFOUNDER`, `20`), así que
 * aquí van ya resueltos; la spec de paridad compara contra los títulos reales,
 * de modo que cambiar la constante y olvidarse de esto sale en rojo en la suite.
 */
export const UNFREEZE_INVARIANT_CATALOG: InvariantCatalogEntry[] = [
  {
    id: "INV-34",
    title:
      "el kit de imágenes está en Cloud Storage (avatares, fondos de rango, neon, portada)",
  },
  { id: "INV-35", title: 'los fundadores tienen `currentRank: "COFOUNDER"`' },
  {
    id: "INV-36",
    title:
      "los fundadores tienen `binaryPercentage: 20` en su nodo de binario",
  },
  {
    id: "INV-37",
    title:
      "`mailTemplates` está resembrada con los asuntos del artefacto desplegado",
  },
];
